// src/components/ProtectedRoute.jsx
// Route wrapper that requires authentication and optionally specific roles

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * ProtectedRoute - Wraps routes that require authentication
 * 
 * Usage:
 *   <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
 *   <Route path="/admin/clients" element={<ProtectedRoute requireAdmin><ClientsPage /></ProtectedRoute>} />
 *   <Route path="/super" element={<ProtectedRoute requireSuperAdmin><SuperAdminPage /></ProtectedRoute>} />
 *   <Route path="/client/:key" element={<ProtectedRoute tenantKey="key"><ClientAdmin /></ProtectedRoute>} />
 */
export default function ProtectedRoute({ 
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requiredRole = null,
  tenantKey = null  // URL param name for tenant access check
}) {
  const { loading, isAuthenticated, isAdmin, isSuperAdmin, hasRole } = useAuth();
  const location = useLocation();

  // Show loading state
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Store the attempted URL for post-login redirect
    localStorage.setItem('auth_redirect', location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check super admin requirement
  if (requireSuperAdmin && !isSuperAdmin()) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>Access Denied</h2>
        <p style={styles.errorText}>
          This page requires super administrator privileges.
        </p>
        <button 
          onClick={() => window.history.back()}
          style={styles.backButton}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin()) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>Access Denied</h2>
        <p style={styles.errorText}>
          This page requires administrator privileges.
        </p>
        <button 
          onClick={() => window.history.back()}
          style={styles.backButton}
        >
          Go Back
        </button>
      </div>
    );
  }

  // Check specific role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div style={styles.errorContainer}>
        <h2 style={styles.errorTitle}>Access Denied</h2>
        <p style={styles.errorText}>
          This page requires the "{requiredRole}" role.
        </p>
        <button 
          onClick={() => window.history.back()}
          style={styles.backButton}
        >
          Go Back
        </button>
      </div>
    );
  }

  // All checks passed
  return children;
}

/**
 * RequireAuth - Simple auth check without role requirements
 */
export function RequireAuth({ children }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

/**
 * RequireAdmin - Requires admin or super_admin role
 */
export function RequireAdmin({ children }) {
  return <ProtectedRoute requireAdmin>{children}</ProtectedRoute>;
}

/**
 * RequireSuperAdmin - Requires super_admin role
 */
export function RequireSuperAdmin({ children }) {
  return <ProtectedRoute requireSuperAdmin>{children}</ProtectedRoute>;
}

const styles = {
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0D1117'
  },
  spinner: {
    width: 40,
    height: 40,
    border: '3px solid rgba(62,207,142,0.2)',
    borderTop: '3px solid #3ECF8E',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  loadingText: {
    marginTop: 16,
    color: '#9ca3af',
    fontSize: 14
  },
  errorContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0D1117',
    padding: 20
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 600,
    color: '#ef4444',
    margin: '0 0 12px 0'
  },
  errorText: {
    fontSize: 16,
    color: '#9ca3af',
    margin: '0 0 24px 0',
    textAlign: 'center'
  },
  backButton: {
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    color: '#ffffff',
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    cursor: 'pointer'
  }
};
